<template>
  <SfModalPortal
    modal-id="reports/create-metric-modal"
    size="s"
    :close-on-backdrop-click="!submitting"
    :close-on-escape="!submitting"
    @opened="onOpened"
    @close="onClose"
  >
    <template #title>{{
      isEdit
        ? I18n.t('report.drawer.insight.create_modal.edit_title')
        : I18n.t('report.drawer.insight.create_modal.title')
    }}</template>

    <template #body>
      <form
        data-testid="create-metric-form"
        class="tw-flex tw-flex-col tw-gap-4"
        @submit.prevent="onSubmit"
      >
        <p
          v-if="hasMoreThanModalShows"
          class="
            tw-m-0
            tw-p-3
            tw-rounded-md
            tw-bg-sf-warning-subtle-2
            tw-text-sm
            tw-text-sf-neutral-dark-1
          "
          data-testid="create-metric-complex-warning"
        >
          {{ I18n.t('report.drawer.insight.create_modal.complex_warning') }}
          <a v-if="editorUrl" :href="editorUrl">
            {{ I18n.t('report.drawer.insight.create_modal.open_editor') }}
          </a>
        </p>

        <div class="tw-flex tw-items-end tw-gap-3">
          <SfInput
            v-model="title"
            focused
            class="tw-flex-1 tw-min-w-0"
            :label="I18n.t('report.drawer.insight.create_modal.title_label')"
            :placeholder="
              I18n.t('report.drawer.insight.create_modal.title_placeholder')
            "
            :disabled="busy"
            data-testid="create-metric-title"
          />
          <SfMetricControls
            v-if="isEdit"
            size="lg"
            class="tw-shrink-0"
            :main-chart="mainChart"
            :pinned="pinnedMetric"
            :main-chart-disabled="busy || store.setupLocked"
            :main-chart-disabled-tooltip="setupLockedTooltip"
            :pinned-disabled="pinDisabled"
            :pinned-disabled-tooltip="pinDisabledTooltip"
            :main-chart-label="
              I18n.t('report.drawer.metric_actions.main_chart')
            "
            :pin-label="I18n.t('report.drawer.metric_actions.highlight')"
            :unpin-label="I18n.t('report.drawer.metric_actions.unhighlight')"
            @update:main-chart="onMainChartChange"
            @update:pinned="pinnedMetric = $event"
          />
        </div>

        <div
          class="tw-flex tw-flex-col tw-gap-1"
          role="radiogroup"
          :aria-labelledby="graphTypeLabelId"
        >
          <span
            :id="graphTypeLabelId"
            class="tw-text-sm tw-font-bold tw-text-sf-gray-dark"
          >
            {{ I18n.t('report.drawer.insight.create_modal.graph_type_label') }}
          </span>
          <div
            class="tw-grid tw-grid-cols-3 tw-gap-2"
            data-testid="create-metric-graph-type"
          >
            <label
              v-for="option in graphTypeOptions"
              :key="option.value"
              class="
                tw-relative
                tw-flex
                tw-flex-col
                tw-items-center
                tw-gap-1
                tw-rounded-lg
                tw-border
                tw-border-solid
                tw-py-3
                tw-px-2
                tw-font-sans
                tw-m-0
                focus-within:tw-ring-2 focus-within:tw-ring-sf-primary
              "
              :class="cardClassFor(option.value)"
            >
              <input
                v-model="graphType"
                type="radio"
                name="create-metric-graph-type"
                class="tw-sr-only"
                :value="option.value"
                :disabled="busy"
                :data-testid="`create-metric-graph-type-${option.value}`"
              />
              <span
                v-if="option.beta"
                class="
                  tw-absolute
                  tw-top-1
                  tw-right-1
                  tw-bg-sf-primary
                  tw-text-white
                  tw-text-[10px]
                  tw-font-bold
                  tw-leading-none
                  tw-px-1
                  tw-py-0.5
                  tw-rounded-sm
                "
              >
                {{ I18n.t('js.report_graph.graph_types.beta') }}
              </span>

              <!-- Time series -->
              <svg
                v-if="option.value === 'linechart'"
                width="28"
                height="25"
                viewBox="0 0 28 25"
                fill="none"
                stroke="currentColor"
                stroke-width="2.8"
                stroke-linecap="round"
              >
                <g transform="translate(1 2)">
                  <polyline points="5.32 7.35 12.42 3.15 16.32 7 24.13 2.1" />
                  <line x1="0.71" y1="0" x2="0.71" y2="21" />
                  <line x1="24.84" y1="21" x2="0.71" y2="21" />
                  <line
                    x1="5.32"
                    y1="12.75"
                    x2="5.32"
                    y2="15.95"
                    opacity="0.3"
                    stroke-width="3.5"
                  />
                  <line
                    x1="16.68"
                    y1="12.25"
                    x2="16.68"
                    y2="15.75"
                    opacity="0.3"
                    stroke-width="3.5"
                  />
                  <line
                    x1="22.35"
                    y1="11.55"
                    x2="22.35"
                    y2="15.75"
                    opacity="0.3"
                    stroke-width="3.5"
                  />
                  <line
                    x1="11.00"
                    y1="9.45"
                    x2="11.00"
                    y2="15.95"
                    opacity="0.3"
                    stroke-width="3.5"
                  />
                </g>
              </svg>

              <!--
                Pie: a rounded-rect path drawn with a stroke wider than the
                shape (28.6) is clipped by a mask of itself, leaving only the
                outer ring visible. Two `use`s share that path — the faint
                full ring + a slice via stroke-dasharray.
              -->
              <svg
                v-else-if="option.value === 'piechart'"
                width="29"
                height="28"
                viewBox="0 0 29 28"
                fill="none"
                stroke="currentColor"
              >
                <defs>
                  <rect
                    :id="pieShapeId"
                    x="0"
                    y="0"
                    width="28.06"
                    height="27.3"
                    rx="13.416"
                  />
                  <mask
                    :id="pieMaskId"
                    maskContentUnits="userSpaceOnUse"
                    maskUnits="objectBoundingBox"
                    x="0"
                    y="0"
                    width="28.06"
                    height="27.3"
                    fill="white"
                  >
                    <use :href="`#${pieShapeId}`" />
                  </mask>
                </defs>
                <g fill-rule="nonzero">
                  <use
                    opacity="0.3"
                    :mask="`url(#${pieMaskId})`"
                    stroke-width="28.6"
                    :href="`#${pieShapeId}`"
                  />
                  <use
                    :mask="`url(#${pieMaskId})`"
                    stroke-width="28.6"
                    stroke-dasharray="53.87 47.84"
                    :href="`#${pieShapeId}`"
                  />
                </g>
              </svg>

              <!-- Waterfall -->
              <svg
                v-else-if="option.value === 'waterfallchart'"
                width="29"
                height="28"
                viewBox="0 0 29 28"
                fill="none"
              >
                <g transform="translate(1 2)" stroke="currentColor">
                  <line
                    x1="0.7"
                    y1="0"
                    x2="0.7"
                    y2="23"
                    stroke-width="2.8"
                    stroke-linecap="round"
                  />
                  <line
                    x1="26"
                    y1="23"
                    x2="0.7"
                    y2="23"
                    stroke-width="2.8"
                    stroke-linecap="round"
                  />
                </g>
                <g transform="translate(1 2)" fill="currentColor">
                  <rect x="3" y="7" width="4.5" height="16" rx="0.5" />
                  <rect
                    x="10"
                    y="2"
                    width="4.5"
                    height="5"
                    rx="0.5"
                    opacity="0.5"
                  />
                  <rect
                    x="17"
                    y="7"
                    width="4.5"
                    height="9"
                    rx="0.5"
                    opacity="0.7"
                  />
                  <rect x="24" y="0" width="4.5" height="23" rx="0.5" />
                </g>
              </svg>

              <span class="tw-text-xs">{{ option.label }}</span>
            </label>
          </div>
        </div>

        <SfInput
          v-model="formula"
          :label="I18n.t('report.drawer.insight.create_modal.formula_label')"
          :placeholder="
            I18n.t('report.drawer.insight.create_modal.formula_placeholder')
          "
          :disabled="busy"
          data-testid="create-metric-formula"
        />

        <p class="tw-text-xs tw-text-sf-neutral-light-1 tw-m-0">
          {{ I18n.t('report.drawer.insight.create_modal.submit_hint') }}
        </p>

        <ul
          v-if="serverErrors.length > 0"
          class="
            tw-list-none
            tw-p-3
            tw-m-0
            tw-rounded-md
            tw-bg-sf-danger-subtle-2
            tw-text-sf-danger-light-2
            tw-text-sm
          "
          data-testid="create-metric-errors"
        >
          <li v-for="(message, index) in serverErrors" :key="index">
            {{ message }}
          </li>
        </ul>

        <!--
          Hidden submit so Enter inside an input reliably triggers the form's
          @submit.prevent. The footer's Create button is outside this form
          (SfModal renders #body and #footer as sibling divs) so it can't
          serve as the implicit-submission target.
        -->
        <button
          type="submit"
          tabindex="-1"
          class="tw-sr-only"
          aria-hidden="true"
        >
          {{ I18n.t('report.drawer.insight.create_modal.submit') }}
        </button>
      </form>
    </template>

    <template #footer>
      <SfButtonDs
        variant="secondary"
        :disabled="busy"
        data-testid="create-metric-cancel"
        @click="onClose"
      >
        {{ I18n.t('report.drawer.insight.create_modal.cancel') }}
      </SfButtonDs>
      <SfButtonDs
        variant="primary"
        :disabled="!canSubmit"
        data-testid="create-metric-submit"
        @click="onSubmit"
      >
        {{ submitLabel }}
      </SfButtonDs>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'

import { SfButtonDs, SfInput } from '@silverfin/ui-components'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import SfMetricControls from '@app/shared/vue/components/sf-metric-controls/sf-metric-controls.vue'
import {
  DEFAULT_GRAPH_TYPE,
  GRAPH_TYPES,
  type GraphType,
} from '@app/application/report/api/drawer'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'CreateMetricModal',
})

const I18n = useI18n()
const store = useReportGraphsDrawer()

// Unique ids for SVG defs/masks (the pie chart's `<use href="#...">` resolves
// against the document, so two mounts of this modal on the same page would
// clash if the ids were hardcoded).
const componentId = useId()
const graphTypeLabelId = `create-metric-graph-type-label-${componentId}`
const pieShapeId = `piechart-shape-${componentId}`
const pieMaskId = `piechart-mask-${componentId}`

const title = ref('')
const formula = ref('')
const graphType = ref<GraphType>(DEFAULT_GRAPH_TYPE)
const submitting = ref(false)
const serverErrors = ref<string[]>([])

// Set when the modal was opened from a pill's Edit button; null means create.
const graphId = ref<number | null>(null)
const valueId = ref<number | null>(null)
const valueCount = ref(0)
const periodCount = ref(0)
const loadingMetric = ref(false)

// What the graph held when the modal opened, so Save can tell a real edit from
// a placement-only change.
const savedTitle = ref('')
const savedFormula = ref('')
const savedGraphType = ref<GraphType>(DEFAULT_GRAPH_TYPE)

// Staged, not applied: the modal's Cancel has to undo these the same way it
// undoes the title. Saving reconciles them against the store.
const mainChart = ref(false)
const pinnedMetric = ref(false)
const savedMainChart = ref(false)
const savedPinnedMetric = ref(false)

const isEdit = computed(() => graphId.value !== null)

const setupLockedTooltip = computed(() =>
  store.locked
    ? I18n.t('report.drawer.locked_tooltip')
    : I18n.t('report.drawer.setup_locked_tooltip')
)

// The cap counts what the drawer already holds, and this modal defers its
// change, so re-pinning a metric that arrived pinned never grows the list.
// Without the savedPinnedMetric check, staging an unpin at the cap left the
// button disabled and the change impossible to take back.
const pinDisabled = computed(
  () =>
    busy.value ||
    store.setupLocked ||
    mainChart.value ||
    (!pinnedMetric.value && !savedPinnedMetric.value && !store.canHighlight)
)

const pinDisabledTooltip = computed(() =>
  store.setupLocked
    ? setupLockedTooltip.value
    : I18n.t('report.drawer.highlighted.cap_reached_tooltip')
)

// A graph cannot be the main chart and a pinned metric at once, so turning the
// main chart on clears the pin rather than letting the server drop it.
function onMainChartChange(next: boolean): void {
  mainChart.value = next
  if (next) pinnedMetric.value = false
}

// The prefill overwrites the fields when it lands, so the form stays inert
// until it does.
const busy = computed(() => submitting.value || loadingMetric.value)

// The modal carries one formula and one chart type, so a graph with extra
// series or period rows holds more than it can show.
const hasMoreThanModalShows = computed(
  () => isEdit.value && (valueCount.value > 1 || periodCount.value > 1)
)

const editorUrl = computed(() => {
  if (graphId.value === null) return null
  return window.Silverfin.Routes.edit_report_template_report_graph_path({
    report_template_id: store.reportTemplateId,
    id: graphId.value,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
})

// Spelled out rather than built from a key fragment so i18n-tasks can still
// see all four strings.
const submitLabel = computed(() => {
  if (isEdit.value) {
    return submitting.value
      ? I18n.t('report.drawer.insight.create_modal.edit_submit_in_progress')
      : I18n.t('report.drawer.insight.create_modal.edit_submit')
  }
  return submitting.value
    ? I18n.t('report.drawer.insight.create_modal.submit_in_progress')
    : I18n.t('report.drawer.insight.create_modal.submit')
})

// SfInput's update:modelValue is typed `string | undefined`; in practice it
// always emits a string, but the `?? ''` keeps the rest of the code from
// having to defend against the type contract.
const trimmedTitle = computed(() => (title.value ?? '').trim())
const trimmedFormula = computed(() => (formula.value ?? '').trim())

// Reuses the existing graph editor's labels (`js.report_graph.graph_types.*`)
// so the modal stays consistent with the standalone editor at /report_graphs/new.
// `linechart` is presented as "Time series" to match the editor's wording.
const GRAPH_TYPE_LABEL_KEYS: Record<GraphType, string> = {
  linechart: 'js.report_graph.graph_types.timeseries',
  piechart: 'js.report_graph.graph_types.piechart',
  waterfallchart: 'js.report_graph.graph_types.waterfallchart',
}
const GRAPH_TYPES_WITH_BETA: ReadonlySet<GraphType> = new Set(['waterfallchart'])

const graphTypeOptions = computed(() =>
  GRAPH_TYPES.map((value) => ({
    value,
    label: I18n.t(GRAPH_TYPE_LABEL_KEYS[value]),
    beta: GRAPH_TYPES_WITH_BETA.has(value),
  }))
)

function cardClassFor(value: GraphType): string[] {
  const stateClasses =
    graphType.value === value
      ? 'tw-bg-sf-primary-subtle tw-text-sf-primary tw-border-sf-primary'
      : 'tw-bg-sf-snow tw-text-sf-neutral-dark-1 tw-border-sf-snow hover:tw-bg-sf-snow-dark'
  const interactionClasses = busy.value
    ? 'tw-opacity-50 tw-cursor-not-allowed'
    : 'tw-cursor-pointer'
  return [stateClasses, interactionClasses]
}

const metricChanged = computed(
  () =>
    !isEdit.value ||
    trimmedTitle.value !== savedTitle.value ||
    trimmedFormula.value !== savedFormula.value ||
    graphType.value !== savedGraphType.value
)

const placementChanged = computed(
  () =>
    mainChart.value !== savedMainChart.value ||
    pinnedMetric.value !== savedPinnedMetric.value
)

// A graph with no series loads an empty formula, which the form cannot accept.
// Gating Save on the formula alone left the main chart and pin controls dead on
// those graphs, and a metric already set as the main chart could not be taken
// back off, so a placement-only change saves without touching the metric.
const canSubmit = computed(() => {
  if (submitting.value || loadingMetric.value) return false
  if (trimmedTitle.value.length > 0 && trimmedFormula.value.length > 0) {
    return true
  }
  return !metricChanged.value && placementChanged.value
})

function resetForm(): void {
  title.value = ''
  formula.value = ''
  graphType.value = DEFAULT_GRAPH_TYPE
  submitting.value = false
  serverErrors.value = []
  graphId.value = null
  valueId.value = null
  mainChart.value = false
  pinnedMetric.value = false
  savedMainChart.value = false
  savedPinnedMetric.value = false
  valueCount.value = 0
  periodCount.value = 0
  loadingMetric.value = false
  savedTitle.value = ''
  savedFormula.value = ''
  savedGraphType.value = DEFAULT_GRAPH_TYPE
}

async function onOpened(
  payload?: { graphId: number } | undefined
): Promise<void> {
  resetForm()
  if (!payload) return

  graphId.value = payload.graphId
  savedMainChart.value = store.pinned?.id === payload.graphId
  savedPinnedMetric.value = store.highlighted.some(
    (g) => g.id === payload.graphId
  )
  mainChart.value = savedMainChart.value
  pinnedMetric.value = savedPinnedMetric.value
  loadingMetric.value = true
  try {
    const metric = await store.fetchMetric(payload.graphId)
    // The modal may have been closed and reopened while the fetch was in
    // flight; only apply the response that matches what is open now.
    if (graphId.value !== payload.graphId) return
    title.value = metric.title
    formula.value = metric.formula
    graphType.value = metric.graphType
    savedTitle.value = metric.title.trim()
    savedFormula.value = metric.formula.trim()
    savedGraphType.value = metric.graphType
    valueId.value = metric.valueId
    valueCount.value = metric.valueCount
    periodCount.value = metric.periodCount
  } catch {
    if (graphId.value !== payload.graphId) return
    serverErrors.value = [
      I18n.t('report.drawer.insight.create_modal.load_error'),
    ]
  } finally {
    if (graphId.value === payload.graphId) loadingMetric.value = false
  }
}

function onClose(): void {
  if (submitting.value) return
  window.modalCitizen.closeModalPortal()
}

async function onSubmit(): Promise<void> {
  if (!canSubmit.value) return

  submitting.value = true
  serverErrors.value = []

  try {
    const input = {
      title: trimmedTitle.value,
      formula: trimmedFormula.value,
      graphType: graphType.value,
    }
    if (graphId.value === null) {
      await store.createMetric(input)
    } else {
      if (metricChanged.value) {
        await store.updateMetric(graphId.value, {
          ...input,
          valueId: valueId.value,
          singleSeries: valueCount.value <= 1,
        })
      }
      await applyStagedPlacement(graphId.value)
    }
    window.modalCitizen.closeModalPortal()
  } catch (error) {
    serverErrors.value = collectServerErrors(error)
  } finally {
    submitting.value = false
  }
}

// Runs after the metric itself is saved. Each store action owns its own
// rollback and toast, so a placement that fails leaves the saved metric alone
// and reports itself.
async function applyStagedPlacement(id: number): Promise<void> {
  if (mainChart.value !== savedMainChart.value) {
    await store.setPinned(id, mainChart.value)
    // Pinning drops the graph from the highlighted list on its own, so there
    // is nothing left for the pin change to do.
    if (mainChart.value) return
  }

  if (pinnedMetric.value !== savedPinnedMetric.value) {
    await store.setHighlighted(id, pinnedMetric.value)
  }
}

function collectServerErrors(error: unknown): string[] {
  if (typeof error !== 'object' || error === null) return []
  const body = (error as { body?: { errors?: Record<string, string[]> } }).body
  const errors = body?.errors
  if (!errors) return []
  return Object.values(errors).flat().filter((message): message is string =>
    typeof message === 'string' && message.length > 0
  )
}
</script>
